// Per-brand AI Lab mock packs + the ONLY lookup surface. findLabEntry() is the
// short-circuit the whole feature hangs on: an exact (normalized) question
// match resolves to its scripted response BEFORE any network — today there is
// no network at all, and when a Lab backend exists the mock check still runs
// first (services/labService.js). findLabEntryById() is the second surface
// (2026-10-08): an entry's `id` is what a DEEP LINK names
// (`/brands/<slug>/lab?lab=<id>`, navigation/labLink), so every id is a
// CONTRACT — unique within its pack, slug-shaped, never renamed and never
// reused for a different answer once a link may be in the wild (a retired
// question keeps its id; two packs may share an id, the slug is in the
// path). Test-pinned in pages/Lab/__tests__/lab.test.js.
import { normalizeQuestion } from "utils/labText";
import { featuredEntries } from "pages/Lab/Starters";

import { ADIDAS_LAB } from "mock/lab/adidas";
import { DR_MARTENS_LAB } from "mock/lab/dr-martens";
import { ESTEE_LAUDER_LAB } from "mock/lab/estee-lauder";
import { MOCKBRAND_LAB } from "mock/lab/mockbrand";
import { STARBUCKS_LAB } from "mock/lab/starbucks";
import { TIFFANY_LAB } from "mock/lab/tiffany";
import { UGG_LAB } from "mock/lab/ugg";

// one pack per demo brand; the workspace SESSION (contexts/SessionContext) picks which one is live
const BY_SLUG = {
  adidas: ADIDAS_LAB,
  "dr-martens": DR_MARTENS_LAB,
  "estee-lauder": ESTEE_LAUDER_LAB,
  mockbrand: MOCKBRAND_LAB,
  starbucks: STARBUCKS_LAB,
  tiffany: TIFFANY_LAB,
  ugg: UGG_LAB,
};

export const labConfig = (slug) => BY_SLUG[slug] || null;

// Exact match after normalization, against the entry's `question` (normalized
// here) and its PRE-normalized `aliases`. Null on miss — the caller renders
// the "supported soon" response, never a dead end.
export function findLabEntry(slug, question) {
  const cfg = labConfig(slug);
  const q = normalizeQuestion(question);
  if (!cfg || !q) return null;
  return (
    cfg.entries.find(
      (e) =>
        normalizeQuestion(e.question) === q || (e.aliases || []).includes(q),
    ) || null
  );
}

// The deep link's lookup: the entry whose `id` the URL names. Null on miss
// — the page lands idle, the param gone, never an error screen.
export function findLabEntryById(slug, id) {
  const cfg = labConfig(slug);
  if (!cfg || !id) return null;
  return cfg.entries.find((e) => e.id === id) || null;
}

// THE WEEKLY PERSPECTIVE's cards (pages/Leaderboard/WeeklyOne) — the
// week's priorities, each with a PREVIEW of its answer. Since 2026-10-22
// a pack authors AS MANY as it wants (user call: "make it possible to add
// multiple … not hardcoded to 1"; ONE card from 2026-10-21, the Weekly
// THREE from 2026-10-14 until then) — `weekly: { sources, cards: [ {
// entry, question?, itemType?, measured, preview }, … ] }`: the
// overline's sources count for the view, then one card per row IN THE
// ORDER THEY RENDER — the entry id (the id contract above; a card naming
// no entry is dropped, never a dead card; a pack whose cards all miss
// shows nothing), its own wording if not the entry's question (then an
// alias of the entry, so the card asks and lands), its own cultural item
// type if not the entry's, the day the preview was measured (ISO, the
// card's dated note — this month's, a different day per pack, user call
// 2026-10-21; the entry's `askedOn` where a card carries none), and the
// preview — `{ trend: { value, label, series }, ring: { value, label },
// bars: { title, rows: [{ label, value, text? }] } }`, three tiles the
// view draws from the answer's own numbers (the value a 0–100 bar, `text`
// what the row prints if not the value). A pack with no `weekly` shows its
// first featured chip (pages/Lab/Starters.featuredEntries) under its own
// words and type, with no preview. Returns `{ sources, cards }` with every
// card's `entry` resolved, so the view can ask with the card's words and a
// test can check the link; null where nothing resolves.
//
// Since 2026-10-26 it also returns the ARCHIVE (user call, from a reference
// screen: "below add archive … where all other labs for current brand that
// are not at top should be"): every OTHER entry of the pack, NEWEST FIRST
// by `askedOn` (pack order among the same day, an undated entry last),
// `featured: false` ones included — `{ sources, cards, archive }`.
export function weeklyFor(cfg) {
  if (!cfg) return null;
  const rows = weeklyCards(cfg);
  if (!rows) return null;
  const top = new Set(rows.cards.map((c) => c.entry.id));
  const archive = (cfg.entries || [])
    .filter((e) => e.question && !top.has(e.id))
    .map((e, i) => ({ e, i }))
    .sort(
      (a, b) =>
        (b.e.askedOn || "").localeCompare(a.e.askedOn || "") || a.i - b.i,
    )
    .map(({ e }) => e);
  return { ...rows, archive };
}

function weeklyCards(cfg) {
  const w = cfg.weekly;
  if (w) {
    const entries = cfg.entries || [];
    const cards = (w.cards || [])
      .map((c) => {
        const entry = entries.find((e) => e.id === c.entry) || null;
        if (!entry) return null;
        return {
          ...c,
          entry,
          question: c.question || entry.question,
          itemType: c.itemType || entry.itemType || null,
          measured: c.measured || entry.askedOn || null,
          preview: c.preview || null,
        };
      })
      .filter(Boolean);
    if (!cards.length) return null;
    return { sources: w.sources || null, cards };
  }
  const [e] = featuredEntries(cfg.entries, 1);
  if (!e) return null;
  return {
    sources: null,
    cards: [
      {
        entry: e,
        question: e.question,
        itemType: e.itemType || null,
        measured: e.askedOn || null,
        preview: null,
      },
    ],
  };
}
